import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuthStore } from './store/authStore';
import { MainLayout } from './components/layout/MainLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { DevicesPage } from './pages/devices/DevicesPage';
import { DeviceDetailPage } from './pages/devices/DeviceDetailPage';
import { AlertsPage } from './pages/alerts/AlertsPage';
import { UsersPage } from './pages/users/UsersPage';
import { HistoryPage } from './pages/history/HistoryPage';
import { CompaniesPage } from './pages/companies/CompaniesPage';
import { ClientsPage } from './pages/clients/ClientsPage';
import { GeofencesPage } from './pages/geofences/GeofencesPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { GeofenceReportPage } from './pages/reports/GeofenceReportPage';
import { RepoAgentPage } from './pages/repo/RepoAgentPage';
import { OAuthClientsPage } from './pages/integrations/OAuthClientsPage';
import { PrivacyPolicyPage } from './pages/legal/PrivacyPolicyPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Platform-operator-only routes (tenant management, API integrations).
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (user?.role !== 'SUPERADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function RepoAgentRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (user?.role !== 'REPO_AGENT') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function DefaultRedirect() {
  const user = useAuthStore((state) => state.user);
  // Redirect repo agents to their special panel
  if (user?.role === 'REPO_AGENT') {
    return <Navigate to="/repo" replace />;
  }
  // Tenant end-customers don't have the company dashboard; land on their devices.
  if (user?.role === 'TENANT_USER') {
    return <Navigate to="/devices" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Pública (sin auth): Google Play exige esta URL para la app móvil. */}
      <Route path="/privacidad" element={<PrivacyPolicyPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DefaultRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="devices/:id" element={<DeviceDetailPage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="geofences" element={<GeofencesPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="reports/geofences" element={<GeofenceReportPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route
          path="companies"
          element={
            <SuperAdminRoute>
              <CompaniesPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="integrations"
          element={
            <SuperAdminRoute>
              <OAuthClientsPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="repo"
          element={
            <RepoAgentRoute>
              <RepoAgentPage />
            </RepoAgentRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
